import React, { useState } from 'react';
import Card from "components/card";
import { MdCheckCircle, MdCancel, MdAccessTime, MdSupervisorAccount, MdTableChart, MdBarcode, MdDateRange } from "react-icons/md";
import UsibrasToast from '../../../components/UsibrasToast';

const sieveSizes = [
  '8mm',
  '6.3mm',
  '4.75mm',
  '4mm',
  '2.8mm',
  '2mm',
  '1.4mm',
  '0.85mm',
  '5.6mm',
  'Bottom'
];

const qualityRows = [
  '1st Quality',
  '2nd Quality',
  '3rd Quality',
  'Sieve 1',
  'Sieve 2',
  'Sieve 3',
  'Sieve 4',
  'Spotted',
  'Dessert',
];

const defectRows = [
  'Peels',
  'Broken',
  'Dirty',
  'Clean',
  'Splits',
  'Pieces',
  'Black Point',
  'Rotten',
  'Wholes',
];

const productTypes = [
  'S1',
  'S2',
  'S3',
  'SP1',
  'SP2',
  'SP3',
  'P1',
  'P2',
  'P3',
  'SSP1',
  'SSP2',
  'SSP3',
  'G1',
  'G2',
  'G3',
  'F1',
  'F2',
  'F3',
  'X1',
  'X2',
  'X3'
];

const batchCodePrefixes = ['BN', 'GH', 'BC', 'BR', 'BS','BK'];

const analystNames = [
  'Edward Abbey',
  'Constance Bofah'
];

const supervisorNames = [
  'Bismark Adutwum',
  'Frank Atidipe'
];

const AnalystEntry1 = () => {
  const [form, setForm] = useState({
    product: '',
    batchCode: '',
    batchCodePrefix: '',
    batchCodeYear: '',
    batchCodeWeek: '',
    supervisor: '',
    analyst: '',
    numMonoblocks: '',
    moisture: '',
    weight: '',
    qualityPercentages: Array(qualityRows.length).fill(''),
    defectPercentages: Array(defectRows.length).fill(''),
    sieveSizes: ['', '', '', ''], // For the four sieve size selections
    recommendation: '',
    rework: '',
    observation: '',
    date: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

  // Function to get current year and week
  const getCurrentYearAndWeek = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2); // Get last 2 digits of year
    const week = Math.ceil((now.getDate() + new Date(now.getFullYear(), 0, 1).getDay()) / 7);
    return { year, week: week.toString().padStart(2, '0') };
  };

  // Function to generate batch code
  const generateBatchCode = (prefix, year, week) => {
    return `${prefix}/${year}/${week}`;
  };

  // Function to handle batch code prefix change
  const handleBatchCodePrefixChange = (prefix) => {
    const { year, week } = getCurrentYearAndWeek();
    const newBatchCode = generateBatchCode(prefix, year, week);
    
    setForm(prev => ({
      ...prev,
      batchCodePrefix: prefix,
      batchCodeYear: year,
      batchCodeWeek: week,
      batchCode: newBatchCode
    }));
  };

  // Function to handle manual batch code editing
  const handleBatchCodeChange = (value) => {
    setForm(prev => ({
      ...prev,
      batchCode: value
    }));
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleQualityPercentageChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      qualityPercentages: prev.qualityPercentages.map((v, i) => i === idx ? value : v)
    }));
  };

  const handleDefectPercentageChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      defectPercentages: prev.defectPercentages.map((v, i) => i === idx ? value : v)
    }));
  };

  const handleSieveSizeChange = (idx, value) => {
    setForm(prev => ({
      ...prev,
      sieveSizes: prev.sieveSizes.map((v, i) => i === idx ? value : v)
    }));
  };

  const handleRecommendation = (value) => {
    setForm(prev => ({ ...prev, recommendation: value }));
  };

  const handleRework = (value) => {
    setForm(prev => ({ ...prev, rework: value }));
  };

  // Print function for thermal printer
  const printReceipt = (formData) => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=300,height=600');
      
      // Check if popup was blocked
      if (!printWindow) {
        console.warn('Popup blocked by browser. Using fallback print method.');
        // Fallback: print current page with receipt content
        printReceiptFallback(formData);
        return;
      }
      
      // Format date
      const formattedDate = new Date(formData.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      // Create the receipt HTML
      const receiptHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Analysis Receipt</title>
            <style>
              @media print {
                @page {
                  size: 80mm auto;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 10px;
                  font-family: 'Courier New', monospace;
                  font-size: 12px;
                  line-height: 1.2;
                  width: 80mm;
                }
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
                width: 80mm;
                margin: 0;
                padding: 10px;
                background: white;
              }
              .header {
                text-align: center;
                border-bottom: 1px dashed #000;
                padding-bottom: 10px;
                margin-bottom: 10px;
              }
              .company-name {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              .receipt-title {
                font-size: 12px;
                margin-bottom: 5px;
              }
              .info-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 5px;
              }
              .label {
                font-weight: bold;
              }
              .value {
                text-align: right;
              }
              .recommendation {
                text-align: center;
                margin: 15px 0;
                padding: 10px;
                border: 2px solid #000;
                border-radius: 5px;
              }
              .approved {
                background-color: #d4edda;
                border-color: #28a745;
              }
              .rejected {
                background-color: #f8d7da;
                border-color: #dc3545;
              }
              .footer {
                text-align: center;
                border-top: 1px dashed #000;
                padding-top: 10px;
                margin-top: 15px;
                font-size: 10px;
              }
              .divider {
                border-top: 1px dashed #000;
                margin: 10px 0;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="company-name">USIBRAS GHANA LIMITED</div>
              <div class="receipt-title">PRODUCT ANALYSIS REPORT</div>
            </div>

            <div class="info-row">
              <span class="label">Product:</span>
              <span class="value">${formData.product}</span>
            </div>

            <div class="info-row">
              <span class="label">Date:</span>
              <span class="value">${formattedDate}</span>
            </div>



            <div class="info-row">
              <span class="label">Batch Code:</span>
              <span class="value">${formData.batchCode}</span>
            </div>

            

            <div class="divider"></div>

            ${formData.moisture ? `
            <div class="info-row">
              <span class="label">Moisture (%):</span>
              <span class="value">${formData.moisture}%</span>
            </div>
            ` : ''}

            ${formData.weight ? `
            <div class="info-row">
              <span class="label">Weight (kg):</span>
              <span class="value">${formData.weight} kg</span>
            </div>
            ` : ''}

            <div class="divider"></div>

            <div class="recommendation ${formData.recommendation === 'approved' ? 'approved' : 'rejected'}">
              <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">
                RECOMMENDATION
              </div>
              <div style="font-size: 16px; font-weight: bold;">
                ${formData.recommendation === 'approved' ? '✓ APPROVED' : '✗ REJECTED'}
              </div>
            </div>

            <div class="divider"></div>

            <div class="info-row">
              <span class="label">Supervisor:</span>
              <span class="value">${formData.supervisor}</span>
            </div>

            <div class="info-row">
              <span class="label">Quality Control:</span>
              <span class="value">${formData.qualityControl}</span>
            </div>

            <div class="footer">
              <div>Generated on: ${new Date().toLocaleString()}</div>
              <div>USIBRAS Ghana Limited</div>
              <div>Quality Control Department</div>
            </div>
          </body>
        </html>
      `;

      // Write the HTML to the new window
      printWindow.document.write(receiptHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = function() {
        printWindow.print();
        printWindow.close();
      };

      // Fallback if onload doesn't work
      setTimeout(() => {
        if (printWindow && printWindow.document && printWindow.document.readyState === 'complete') {
          printWindow.print();
          printWindow.close();
        }
      }, 500);
      
    } catch (error) {
      console.error('Error in printReceipt:', error);
      // Fallback to alternative print method
      printReceiptFallback(formData);
    }
  };

  // Fallback print function
  const printReceiptFallback = (formData) => {
    try {
      // Format date
      const formattedDate = new Date(formData.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });



      // Create a temporary div with the receipt content
      const receiptDiv = document.createElement('div');
      receiptDiv.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: 80mm;
        font-family: 'Courier New', monospace;
        font-size: 12px;
        line-height: 1.2;
        background: white;
        padding: 10px;
        border: 1px solid #000;
      `;
      
      receiptDiv.innerHTML = `
        <div style="text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px;">
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">USIBRAS GHANA LIMITED</div>
          <div style="font-size: 12px; margin-bottom: 5px;">PRODUCT ANALYSIS REPORT</div>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Product:</span>
          <span>${formData.product}</span>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Date:</span>
          <span>${formattedDate}</span>
        </div>



        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Batch Code:</span>
          <span>${formData.batchCode}</span>
        </div>

        

        <div style="border-top: 1px dashed #000; margin: 10px 0;"></div>

        ${formData.moisture ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Moisture (%):</span>
          <span>${formData.moisture}%</span>
        </div>
        ` : ''}

        ${formData.weight ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Weight (kg):</span>
          <span>${formData.weight} kg</span>
        </div>
        ` : ''}

        <div style="border-top: 1px dashed #000; margin: 10px 0;"></div>

        <div style="text-align: center; margin: 15px 0; padding: 10px; border: 2px solid #000; border-radius: 5px; background-color: ${formData.recommendation === 'approved' ? '#d4edda' : '#f8d7da'}; border-color: ${formData.recommendation === 'approved' ? '#28a745' : '#dc3545'};">
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">RECOMMENDATION</div>
          <div style="font-size: 16px; font-weight: bold;">${formData.recommendation === 'approved' ? '✓ APPROVED' : '✗ REJECTED'}</div>
        </div>

        <div style="border-top: 1px dashed #000; margin: 10px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Supervisor:</span>
          <span>${formData.supervisor}</span>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: bold;">Quality Control:</span>
          <span>${formData.qualityControl}</span>
        </div>

        <div style="text-align: center; border-top: 1px dashed #000; padding-top: 10px; margin-top: 15px; font-size: 10px;">
          <div>Generated on: ${new Date().toLocaleString()}</div>
          <div>USIBRAS Ghana Limited</div>
          <div>Quality Control Department</div>
        </div>
      `;

      // Add to document
      document.body.appendChild(receiptDiv);

      // Print
      window.print();

      // Remove from document after printing
      setTimeout(() => {
        document.body.removeChild(receiptDiv);
      }, 1000);

    } catch (error) {
      console.error('Error in printReceiptFallback:', error);
      // Show toast message as final fallback
      setToast({
        show: true,
        message: 'Print failed. Please manually print the analysis data.',
        type: 'warning'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setToast({ show: false, message: '', type: 'info' });

    // Validate recommendation selection
    if (!form.recommendation) {
      setToast({
        show: true,
        message: 'Please select a recommendation (Approved or Rejected) before submitting.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Map form data to API parameters
      const apiData = {
        product: form.product,
        batch_code: form.batchCode,
        supervisor: form.supervisor,
        analyst: form.analyst,
        num_monoblocks: parseInt(form.numMonoblocks) || 0,
        
        moisture: form.moisture ? parseFloat(form.moisture) : null, // Optional
        weight: form.weight ? parseFloat(form.weight) : null, // Optional
        
        first_quality_percentage: parseFloat(form.qualityPercentages[0]) || 0,
        second_quality_percentage: parseFloat(form.qualityPercentages[1]) || 0,
        third_quality_percentage: parseFloat(form.qualityPercentages[2]) || 0,
        
        // Additional quality percentage fields
        "1st_quality_per": parseFloat(form.qualityPercentages[0]) || 0,
        "2nd_quality_per": parseFloat(form.qualityPercentages[1]) || 0,
        "3rd_quality_per": parseFloat(form.qualityPercentages[2]) || 0,
        
        sieve_1_percentage: parseFloat(form.qualityPercentages[3]) || 0,
        sieve_1_size: form.sieveSizes[0] || '',
        sieve_2_percentage: parseFloat(form.qualityPercentages[4]) || 0,
        sieve_2_size: form.sieveSizes[1] || '',
        sieve_3_percentage: parseFloat(form.qualityPercentages[5]) || 0,
        sieve_3_size: form.sieveSizes[2] || '',
        sieve_4_percentage: parseFloat(form.qualityPercentages[6]) || 0,
        sieve_4_size: form.sieveSizes[3] || '',
        
        spotted_percentage: parseFloat(form.qualityPercentages[7]) || 0,
        dessert_percentage: parseFloat(form.qualityPercentages[8]) || 0,
        
        peels_percentage: parseFloat(form.defectPercentages[0]) || 0,
        broken_percentage: parseFloat(form.defectPercentages[1]) || 0,
        dirty_percentage: parseFloat(form.defectPercentages[2]) || 0,
        clean_percentage: parseFloat(form.defectPercentages[3]) || 0,
        splits_percentage: parseFloat(form.defectPercentages[4]) || 0,
        pieces_percentage: parseFloat(form.defectPercentages[5]) || 0,
        black_point_percentage: parseFloat(form.defectPercentages[6]) || 0,
        rotten_percentage: parseFloat(form.defectPercentages[7]) || 0,
        wholes_percentage: parseFloat(form.defectPercentages[8]) || 0,
        
        recommendation: form.recommendation,
        rework: form.rework,
        observation: form.observation,
        entry_date: form.date,
        quality_manager: "Gifty Asante"
      };

      const response = await fetch('https://usibras-web-backend.onrender.com/api/analyst-pieces', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setToast({
          show: true,
          message: data.message || 'Analysis submitted successfully!',
          type: 'success'
        });
        
        // printReceipt(form); // <-- Call directly here
        
                 // Reset form after successful submission
         setForm({
           product: '',
           batchCode: '',
           batchCodePrefix: '',
           batchCodeYear: '',
           batchCodeWeek: '',
           supervisor: '',
           analyst: '',
           numMonoblocks: '',
          moisture: '',
          weight: '',
          qualityPercentages: Array(qualityRows.length).fill(''),
          defectPercentages: Array(defectRows.length).fill(''),
          sieveSizes: ['', '', '', ''],
          recommendation: '',
          rework: '',
          observation: '',
          date: ''
        });
      } else {
        setToast({
          show: true,
          message: data.message || 'Failed to submit analysis. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting analysis:', error);
      setToast({
        show: true,
        message: 'Error: Failed to connect to server. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-2 sm:mt-5 h-full flex justify-center px-2 sm:px-4 lg:px-6">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      <Card extra="relative w-full max-w-4xl p-4 sm:p-6 lg:p-8">
        <div className="absolute top-4 right-4">
          <span className="inline-block bg-gray-100 dark:bg-navy-800 text-navy-700 dark:text-white text-xs font-bold px-2 py-1 rounded shadow-sm">QC-FR-020</span>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-navy-700 dark:text-white mb-1">Product Analysis Entry(Pieces)</h1>
            {/* <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">QC-FR-020</p> */}
          </div>

          {/* Product Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium mb-1">Product</label>
              <select 
                name="product" 
                value={form.product} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                required
              >
                <option value="">Select a product type</option>
                {productTypes.map((productType) => (
                  <option key={productType} value={productType}>
                    {productType}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Batch Code</label>
              <div className="flex gap-2">
                <select 
                  value={form.batchCodePrefix} 
                  onChange={(e) => handleBatchCodePrefixChange(e.target.value)}
                  className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                  required
                >
                  <option value="">Prefix</option>
                  {batchCodePrefixes.map((prefix) => (
                    <option key={prefix} value={prefix}>
                      {prefix}
                    </option>
                  ))}
                </select>
                <input 
                  name="batchCode" 
                  value={form.batchCode} 
                  onChange={(e) => handleBatchCodeChange(e.target.value)}
                  placeholder="BN/25/33"
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                  required 
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Supervisor</label>
              <select 
                name="supervisor" 
                value={form.supervisor} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                required
              >
                <option value="">Select a supervisor</option>
                {supervisorNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Analyst</label>
              <select 
                name="analyst" 
                value={form.analyst} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                required
              >
                <option value="">Select an analyst</option>
                {analystNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">No. of Monoblocks</label>
              <select 
                name="numMonoblocks" 
                value={form.numMonoblocks} 
                onChange={handleInput} 
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
                required
              >
                <option value="">Select number of monoblocks</option>
                {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Analysis Section */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4">Analysis of Product</h2>
            
            {/* Moisture and Weight Input Fields (Optional) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Moisture (%) <span className="text-gray-500 text-xs">(Optional)</span></label>
                <input 
                  name="moisture" 
                  value={form.moisture} 
                  onChange={handleInput} 
                  type="number" 
                  min="0" 
                  max="100"
                  step="0.1"
                  placeholder="Enter moisture %"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Weight (kg) <span className="text-gray-500 text-xs">(Optional)</span></label>
                <input 
                  name="weight" 
                  value={form.weight} 
                  onChange={handleInput} 
                  type="number" 
                  min="0" 
                  step="0.01"
                  placeholder="Enter weight in kg"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" 
                />
              </div>
            </div>

            {/* Analysis Table - Mobile Responsive */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-700">
                <thead className="bg-gray-50 dark:bg-navy-800">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Quality</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Size</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">%</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">Defects</th>
                    <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500">%</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-navy-900 divide-y divide-gray-100 dark:divide-navy-800">
                  {Array.from({ length: Math.max(qualityRows.length, defectRows.length) }).map((_, idx) => (
                    <tr key={idx}>
                      <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                        {qualityRows[idx] || ''}
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        {idx >= 3 && idx <= 6 && qualityRows[idx] && qualityRows[idx].startsWith('Sieve') ? (
                          <select
                            value={form.sieveSizes[idx - 3]}
                            onChange={e => handleSieveSizeChange(idx - 3, e.target.value)}
                            className="w-20 sm:w-24 rounded border border-gray-200 px-1 sm:px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                          >
                            <option value="">Select</option>
                            {sieveSizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        {qualityRows[idx] !== undefined && (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={form.qualityPercentages[idx]}
                            onChange={e => handleQualityPercentageChange(idx, e.target.value)}
                            className="w-16 sm:w-20 rounded border border-gray-200 px-1 sm:px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                          />
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-navy-700 dark:text-white">
                        {defectRows[idx] || ''}
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        {defectRows[idx] !== undefined && (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={form.defectPercentages[idx]}
                            onChange={e => handleDefectPercentageChange(idx, e.target.value)}
                            className="w-16 sm:w-20 rounded border border-gray-200 px-1 sm:px-2 py-1 text-xs sm:text-sm dark:bg-navy-700 dark:text-white"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recommendation */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4">
              Recommendation <span className="text-red-500">*</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
              <button
                type="button"
                onClick={() => handleRecommendation('approved')}
                className={`flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow transition-all text-base sm:text-lg font-semibold border-2 focus:outline-none
                  ${form.recommendation === 'approved'
                    ? 'bg-green-500 border-green-700 text-white scale-105'
                    : 'bg-white dark:bg-navy-800 border-gray-200 dark:border-navy-700 text-green-600 hover:bg-green-50 dark:hover:bg-green-900'}
                `}
              >
                <MdCheckCircle className="text-xl sm:text-2xl" /> Approved
              </button>
              <button
                type="button"
                onClick={() => handleRecommendation('rejected')}
                className={`flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow transition-all text-base sm:text-lg font-semibold border-2 focus:outline-none
                  ${form.recommendation === 'rejected'
                    ? 'bg-red-500 border-red-700 text-white scale-105'
                    : 'bg-white dark:bg-navy-800 border-gray-200 dark:border-navy-700 text-red-600 hover:bg-red-50 dark:hover:bg-red-900'}
                `}
              >
                <MdCancel className="text-xl sm:text-2xl" /> Rejected
              </button>
            </div>
            {!form.recommendation && (
              <p className="text-red-500 text-sm mt-2 flex items-center">
                <MdCancel className="mr-1" />
                Please select either Approved or Rejected
              </p>
            )}
          </div>

          {/* Rework */}
          <div>
            <h2 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 sm:mb-4">Rework</h2>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rework"
                  value="Yes"
                  checked={form.rework === 'Yes'}
                  onChange={(e) => handleRework(e.target.value)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-navy-700 dark:text-white">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rework"
                  value="No"
                  checked={form.rework === 'No'}
                  onChange={(e) => handleRework(e.target.value)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-navy-700 dark:text-white">No</span>
              </label>
            </div>
          </div>

          {/* Observation */}
          <div>
            <label className="block text-sm font-medium mb-1">Observation</label>
            <textarea
              name="observation"
              value={form.observation}
              onChange={handleInput}
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Footer Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <input name="date" value={form.date} onChange={handleInput} type="date" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:bg-navy-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Quality Manager</label>
              <div className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-navy-600">
                Gifty Asante
              </div>
            </div>
          </div>

          {/* Document issue footer */}
          <div className="text-center mt-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Issue number: 6 — Date Issued: 30/09/2025</p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center sm:justify-end pt-4">
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={`w-full sm:w-auto rounded-lg px-6 py-3 font-semibold transition-colors text-base sm:text-sm ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Analysis'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AnalystEntry1; 